import { defineStore } from 'pinia'
import { computed } from 'vue'
import { isManagedByAgency } from '@dashboard/modules/onboarding/services/managedAgencyOnboarding'
import { useAuthStore } from '@dashboard/stores/authStore'
import { useConfigStore } from '@dashboard/stores/configStore'
import { useTeamStore } from '@dashboard/stores/teamStore'
import { parseOnboardingAiContext } from '../services/activationResearchContext'
import { hasSkippedGuidedProjectSetup } from '../services/guidedProjectSetupBypass'
import { readPersistedOnboardingJourney } from '../services/onboardingJourney'
import { useReportStore } from '@dashboard/modules/dashboard/stores/reportStore'
import { visibleThingsInFlow } from '@dashboard/modules/flow/services/flowUtilities'
import { useFlowStore } from '@dashboard/modules/flow/stores/flowStore'
import { useIntegrationStore } from '@dashboard/modules/integration/stores/integrationStore'
import { useCampaignStore } from '@dashboard/modules/personalization/stores/campaignStore'
import { useWidgetStore } from '@dashboard/modules/widget/stores/widgetStore'
import { storeToRefs } from 'pinia'

export const useOnboardingStore = defineStore('onboarding', () => {
  const configStore = useConfigStore()
  const { fullResponse, currentTeam, currentTeamPid } = storeToRefs(configStore)
  const authStore = useAuthStore()
  const { user } = storeToRefs(authStore)
  const { teams } = storeToRefs(useTeamStore())

  const managedByAgency = computed(() => isManagedByAgency(user.value))

  const integrationStore = useIntegrationStore()
  const { partners, integratedPartner } = storeToRefs(integrationStore)
  const hasIntegrated = computed(() => {
    if (!partners.value.length) {
      return false
    }
    return integratedPartner.value
  })

  const reportStore = useReportStore()
  const { dataLastReceived } = storeToRefs(reportStore)
  const hasAddedScript = computed(() => {
    if (!dataLastReceived.value) {
      return true
    }
    return dataLastReceived.value?.last_update !== null
  })

  // hasAddedScript is optimistic while the reporting query is still in
  // flight (avoids flashing install banners at existing users). That makes
  // it unsafe for anything persisted — launch plan items seeded or
  // auto-completed from it would record a false positive forever. Use this
  // resolved-only signal for persisted completion state.
  const hasConfirmedScript = computed(() => !!dataLastReceived.value && dataLastReceived.value.last_update !== null)

  const flowStore = useFlowStore()
  const { flows } = storeToRefs(flowStore)
  const hasCreatedFlow = computed(
    () => flows.value.filter((f) => visibleThingsInFlow(f.funnelDefinition).length > 0).length > 0
  )

  const campaignStore = useCampaignStore()
  const { campaigns } = storeToRefs(campaignStore)

  const widgetStore = useWidgetStore()
  const { widgets } = storeToRefs(widgetStore)
  const hasCreatedWidget = computed(
    () => widgets.value.filter((widget) => widget.contentId && widget.isEnabled).length > 0
  )

  // Don't count the initial auto-publish (blank config) as a real publish.
  // A real publish means the stable config has meaningful content.
  const hasPublished = computed(() => {
    const response = fullResponse.value
    if (!response || typeof response !== 'object' || Array.isArray(response)) {
      return false
    }

    const stableConfig = response.stable
    if (!stableConfig || typeof stableConfig !== 'object' || Array.isArray(stableConfig)) {
      return false
    }
    const hasDimensions = Array.isArray(stableConfig.dimensions) && stableConfig.dimensions.length > 0
    const hasCampaigns = Array.isArray(stableConfig.campaigns) && stableConfig.campaigns.length > 0
    const hasFlows =
      Array.isArray(stableConfig.rightCta?.offerFunnels) &&
      stableConfig.rightCta.offerFunnels.some(
        (funnel) =>
          !!funnel.funnelDefinition &&
          typeof funnel.funnelDefinition === 'object' &&
          visibleThingsInFlow(funnel.funnelDefinition).length > 0
      )

    return hasDimensions || hasCampaigns || hasFlows
  })

  // Any campaign counts: a campaign-only project has nothing for the flow-based setup checklist to guide.
  const hasMeaningfulFirstWinProgress = computed(
    () =>
      !!(
        hasPublished.value ||
        campaigns.value.length > 0 ||
        (hasConfirmedScript.value && (hasCreatedFlow.value || hasCreatedWidget.value))
      )
  )

  const shouldShowDashboardExperience = computed(() => {
    if (managedByAgency.value) {
      return true
    }
    if (!partners.value.length) {
      return true
    }
    return hasMeaningfulFirstWinProgress.value
  })

  const shouldShowDashboardOnboardingFallback = computed(
    () => !managedByAgency.value && !shouldShowDashboardExperience.value
  )

  const shouldResumeOnboarding = () => {
    const team = currentTeam.value
    if (!team || String(currentTeamPid.value) !== String(team.pid) || !fullResponse.value) {
      return false
    }
    if (teams.value.length !== 1 || managedByAgency.value || user.value?.is_agency) {
      return false
    }
    if (hasSkippedGuidedProjectSetup(team.pid) || hasMeaningfulFirstWinProgress.value) {
      return false
    }
    // A draft is evidence that a playbook or manual build has already been started.
    if (flows.value.length || campaigns.value.length || hasCreatedWidget.value) {
      return false
    }

    return (
      parseOnboardingAiContext(team.ai_context).playbookRecommendations !== null ||
      readPersistedOnboardingJourney(team.pid) !== null
    )
  }

  return {
    managedByAgency,
    shouldShowDashboardExperience,
    shouldShowDashboardOnboardingFallback,
    shouldResumeOnboarding,
    hasIntegrated,
    hasAddedScript,
    hasConfirmedScript,
    hasCreatedFlow,
    hasCreatedWidget,
    hasPublished,
    hasMeaningfulFirstWinProgress
  }
})
